import React, { useMemo, useState } from "react";
import { ArrowLeft20Regular } from "@fluentui/react-icons";
import { labelForUserKey, type AppUser } from "../../services/userService";
import { chatService, type ChatConversation } from "../../services/chatService";
import { ChatComposer, type MentionMember } from "./ChatComposer";
import { InitialsAvatar } from "./chatUi";
import { INK, INK2, LINE, PLUM, THREAD_BG, CHAT_PAD_X } from "./chatTheme";

type Props = {
  users: AppUser[];
  currentUserId: string | null;
  onBack?: () => void;
  /** Die Nachricht ist raus — die Direktnachrichten, in denen sie gelandet ist. */
  onSent: (conversations: ChatConversation[]) => void;
};

const newUuid = (): string =>
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.floor(Math.random() * 1e9)}`;

/**
 * Nachricht an mehrere hinter dem „+" (PO 05.10.2026): kein Formular, man
 * schreibt los und nennt die Empfänger per @ im Text. Es entsteht KEIN
 * gemeinsamer Chat — jeder Genannte bekommt die Nachricht als Direktnachricht
 * vom Absender, Antworten laufen dort 1:1 weiter.
 */
export const NewChatDraft: React.FC<Props> = ({ users, currentUserId, onBack, onSent }) => {
  const [fehler, setFehler] = useState<string | null>(null);
  const [gesendetAn, setGesendetAn] = useState<string[] | null>(null);

  const nameOf = (u: AppUser): string =>
    [u.firstName, u.lastName].filter(Boolean).join(" ").trim() ||
    u.name ||
    u.username ||
    u.email ||
    labelForUserKey(u.id, users);

  const personen = useMemo<MentionMember[]>(
    () =>
      users
        .filter((u) => u.id !== currentUserId)
        .map((u) => ({
          userId: u.id,
          name: nameOf(u),
          kuerzel: u.username || u.email?.split("@")[0] || null,
        }))
        .sort((a, b) => a.name.localeCompare(b.name, "de", { sensitivity: "base" })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [users, currentUserId],
  );

  const senden = async (text: string, gewaehlteIds: string[]) => {
    // Empfänger: per Vorschlag gewählte Erwähnungen plus von Hand getippte Kürzel
    // („@sah" oder „(@sah)" ohne die Vorschlagsliste zu benutzen).
    const getippt = new Set(
      Array.from(text.matchAll(/@([\p{L}\p{N}._-]+)/gu), (m) => m[1].toLowerCase()),
    );
    const erwaehnte = personen.filter(
      (p) => gewaehlteIds.includes(p.userId) || (!!p.kuerzel && getippt.has(p.kuerzel.toLowerCase())),
    );
    if (erwaehnte.length === 0) {
      setFehler("Bitte mindestens eine Person mit @ erwähnen — an sie geht die Nachricht.");
      // Der Composer behält den Text, wenn das Senden scheitert.
      throw new Error("Keine Empfänger");
    }
    setFehler(null);
    setGesendetAn(null);
    // Je Empfänger die Direktnachricht holen/anlegen und die Nachricht dort senden.
    const ergebnisse = await Promise.allSettled(
      erwaehnte.map(async (p) => {
        const conv = await chatService.getOrCreateDm(p.userId);
        await chatService.sendMessage(conv.id, { body: text, clientMsgId: newUuid() });
        return conv;
      }),
    );
    const zugestellt = ergebnisse.flatMap((r) => (r.status === "fulfilled" ? [r.value] : []));
    const ok = erwaehnte.filter((_, i) => ergebnisse[i].status === "fulfilled").map((p) => p.name);
    const offen = erwaehnte.filter((_, i) => ergebnisse[i].status === "rejected").map((p) => p.name);
    if (ok.length) setGesendetAn(ok);
    if (offen.length) {
      // Kein Wurf: sonst bliebe der Text stehen und ein zweiter Versuch ginge
      // an die bereits Erreichten doppelt raus.
      setFehler(`Nicht zugestellt an: ${offen.join(", ")}. Bitte dort noch einmal senden.`);
    }
    if (zugestellt.length) onSent(zugestellt);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, padding: `10px ${CHAT_PAD_X}px`, borderBottom: `1px solid ${LINE}`, flexShrink: 0 }}>
        {onBack && (
          <button type="button" onClick={onBack} aria-label="Zurück" style={{ border: "none", background: "transparent", cursor: "pointer", color: INK }}>
            <ArrowLeft20Regular />
          </button>
        )}
        <InitialsAvatar name="Neue Nachricht" size={40} square bg={PLUM} label="+" />
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 800, color: INK, fontSize: 16, fontFamily: "'Playfair Display', Georgia, serif" }}>
            Neue Nachricht an mehrere
          </div>
          <div style={{ color: INK2, fontSize: 12 }}>Empfänger mit @ im Text nennen</div>
        </div>
      </div>

      <div style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: `8px ${CHAT_PAD_X}px 4px`, background: THREAD_BG, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ maxWidth: 380, textAlign: "center", color: INK2, fontSize: 13.5, lineHeight: 1.5 }}>
          Schreib deine Nachricht und nenne die Empfänger mit <b>@</b> — zum Beispiel
          „@sah @cll bitte kurz melden“. Jede genannte Person bekommt sie als
          Direktnachricht von dir.
        </div>
      </div>

      <div style={{ flexShrink: 0, padding: `0 ${CHAT_PAD_X}px 10px` }}>
        {gesendetAn && (
          <div role="status" style={{ margin: "8px 0", padding: "6px 10px", borderRadius: 8, background: "#eef7ef", color: "#1f5a2b", fontSize: 12.5 }}>
            Gesendet an: {gesendetAn.join(", ")}
          </div>
        )}
        {fehler && (
          <div role="alert" style={{ margin: "8px 0", padding: "6px 10px", borderRadius: 8, background: "#fdf0f0", color: "#8a2626", fontSize: 12.5 }}>
            {fehler}
          </div>
        )}
        <ChatComposer
          autoFocus
          members={personen}
          placeholder="Nachricht mit @Empfängern…"
          onSubmit={({ text, mentionedUserIds }) => senden(text, mentionedUserIds)}
        />
      </div>
    </div>
  );
};

export default NewChatDraft;
